import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Mail, RefreshCw, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { CopyField } from '../../../../components/design-system/common/CopyField';
import { StatusBadge } from '../../../../components/design-system/admin/StatusBadge';
import {
    TRADE_STATUSES, TradeIntroLang, TradeProspect, TradeStatus, useTradeApi,
} from '../../../../common/hooks/use-trade-api.hook';
import {
    STATUS_TONE, activityLabel, formatKm, headcountLabel, mailtoHref, reasonLabel, sinceYear, webSearchHref,
} from '../trade-format';

const INTRO_LANGS: TradeIntroLang[] = ['fr', 'en', 'es', 'ar'];

/** One professional: the registry's facts, why they rank, the introduction, and the vendor's own notes. */
export const ProspectSheet = ({
    prospectId,
    onClose,
    onChanged,
}: {
    prospectId: string | null;
    onClose: () => void;
    onChanged: (p: TradeProspect) => void;
}) => {
    const { t, i18n } = useTranslation();
    const api = useTradeApi();
    const [prospect, setProspect] = useState<TradeProspect | null>(null);
    const [notes, setNotes] = useState('');
    const [writing, setWriting] = useState(false);
    const [savingNotes, setSavingNotes] = useState(false);
    const [lang, setLang] = useState<TradeIntroLang>('fr');

    useEffect(() => {
        if (!prospectId) { setProspect(null); return; }
        let cancelled = false;
        api.get(prospectId)
            .then((p) => { if (!cancelled) { setProspect(p); setNotes(p.notes ?? ''); setLang((p.intro?.lang as TradeIntroLang) || 'fr'); } })
            .catch(() => { if (!cancelled) toast.error(t('TRADE.ERRORS.LOAD', 'Could not load this prospect.')); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prospectId]);

    const apply = (p: TradeProspect) => { setProspect(p); onChanged(p); };

    const setStatus = async (status: TradeStatus) => {
        if (!prospect || status === prospect.status) return;
        try {
            apply(await api.update(prospect._id, { status }));
        } catch {
            toast.error(t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        }
    };

    const saveNotes = async () => {
        if (!prospect || notes === (prospect.notes ?? '')) return;
        setSavingNotes(true);
        try {
            apply(await api.update(prospect._id, { notes }));
            toast.success(t('TRADE.DRAWER.NOTES_SAVED', 'Notes saved.'));
        } catch {
            toast.error(t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        } finally {
            setSavingNotes(false);
        }
    };

    const writeIntro = async () => {
        if (!prospect) return;
        setWriting(true);
        try {
            apply(await api.writeIntro(prospect._id, lang));
        } catch {
            toast.error(t('TRADE.ERRORS.INTRO', 'The introduction could not be written. Try again.'));
        } finally {
            setWriting(false);
        }
    };

    const p = prospect;
    const locale = i18n.language || 'en';
    const fact = (label: string, value: ReactNode) => (
        <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
            <span className="shrink-0 text-muted-foreground">{label}</span>
            <span className="min-w-0 text-end">{value}</span>
        </div>
    );

    return (
        <Sheet open={!!prospectId} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full gap-0 overflow-y-auto p-0 data-[side=right]:sm:max-w-[560px]" data-testid="trade-prospect-sheet">
                {!p ? (
                    <div className="flex h-40 items-center justify-center"><Spinner /></div>
                ) : (
                    <>
                        <SheetHeader className="border-b p-6 pe-14">
                            <SheetTitle className="text-lg">{p.tradeName || p.name}</SheetTitle>
                            <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                {p.tradeName && <span>{p.name}</span>}
                                <span>{activityLabel(p.activity.code, t, p.activity.label)}</span>
                                <StatusBadge tone={STATUS_TONE[p.status]}>{t(`TRADE.STATUS.${p.status.toUpperCase()}`, p.status)}</StatusBadge>
                            </SheetDescription>
                        </SheetHeader>

                        <div className="flex flex-col gap-6 p-6">
                            <section>
                                <h3 className="mb-2 text-sm font-medium">{t('TRADE.DRAWER.STATUS', 'Where you are with them')}</h3>
                                <ToggleGroup
                                    type="single"
                                    variant="outline"
                                    size="sm"
                                    value={p.status}
                                    onValueChange={(v) => v && setStatus(v as TradeStatus)}
                                    className="flex-wrap"
                                >
                                    {TRADE_STATUSES.map((s) => (
                                        <ToggleGroupItem key={s} value={s} data-testid={`trade-status-${s}`}>
                                            {t(`TRADE.STATUS.${s.toUpperCase()}`, s)}
                                        </ToggleGroupItem>
                                    ))}
                                </ToggleGroup>
                            </section>

                            <section data-testid="trade-intro">
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                    <h3 className="text-sm font-medium">{t('TRADE.DRAWER.INTRO', 'Introduction')}</h3>
                                    <div className="flex items-center gap-2">
                                        <Select value={lang} onValueChange={(v) => setLang(v as TradeIntroLang)}>
                                            <SelectTrigger size="sm" className="w-32" aria-label={t('TRADE.DRAWER.LANGUAGE', 'Language')}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {INTRO_LANGS.map((l) => (
                                                    <SelectItem key={l} value={l}>{t(`TRADE.LANG.${l.toUpperCase()}`, l)}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {p.intro && (
                                            <Button variant="outline" size="sm" onClick={writeIntro} disabled={writing}>
                                                {writing ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                                                {t('TRADE.DRAWER.REWRITE', 'Rewrite')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {!p.intro ? (
                                    <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-4">
                                        <p className="text-sm text-muted-foreground">
                                            {t('TRADE.DRAWER.INTRO_EMPTY', 'A short email and the first words of a phone call, written for this professional: what you sell, how close you are, and a place for your trade offer.')}
                                        </p>
                                        <Button onClick={writeIntro} disabled={writing} data-testid="trade-write-intro">
                                            {writing ? <Spinner data-icon="inline-start" /> : <Sparkles data-icon="inline-start" />}
                                            {t('TRADE.DRAWER.WRITE_INTRO', 'Write an introduction')}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        <CopyField label={t('TRADE.DRAWER.SUBJECT', 'Subject')} value={p.intro.subject} multiline prose />
                                        <CopyField label={t('TRADE.DRAWER.EMAIL', 'Email')} value={p.intro.email} multiline prose />
                                        {p.intro.phone && <CopyField label={t('TRADE.DRAWER.PHONE', 'Phone opener')} value={p.intro.phone} multiline prose />}
                                        {p.intro.hasPlaceholder && (
                                            <p className="text-xs text-muted-foreground">
                                                {t('TRADE.DRAWER.PLACEHOLDER_HINT', 'Replace the text in brackets with your trade offer before sending — or write your offer once in Settings and every introduction will use it. We never invent a discount.')}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap gap-2">
                                            <Button asChild variant="outline" size="sm">
                                                <a href={mailtoHref(p.intro.subject, p.intro.email)}>
                                                    <Mail data-icon="inline-start" />
                                                    {t('TRADE.DRAWER.OPEN_EMAIL', 'Open in my email')}
                                                </a>
                                            </Button>
                                            {p.status === 'new' && (
                                                <Button variant="outline" size="sm" onClick={() => setStatus('contacted')}>
                                                    {t('TRADE.DRAWER.MARK_CONTACTED', 'Mark as contacted')}
                                                </Button>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {p.intro.model === 'template'
                                                ? t('TRADE.DRAWER.WRITTEN_TEMPLATE', 'Written from a template.')
                                                : t('TRADE.DRAWER.WRITTEN_AI', 'Drafted by AI — read it before you send it.')}
                                        </p>
                                    </div>
                                )}
                            </section>

                            <Separator />

                            <section>
                                <h3 className="mb-1 text-sm font-medium">{t('TRADE.DRAWER.WHY', 'Why they rank here')}</h3>
                                <p className="mb-2 text-xs text-muted-foreground">{t('TRADE.DRAWER.SCORE', 'Score {{score}} out of 100', { score: p.score })}</p>
                                <ul className="flex flex-wrap gap-1.5">
                                    {p.reasons.map((r) => (
                                        <li key={r} className="rounded-md border px-2 py-0.5 text-xs">{reasonLabel(r, t)}</li>
                                    ))}
                                </ul>
                            </section>

                            <section>
                                <h3 className="mb-1 text-sm font-medium">{t('TRADE.DRAWER.COMPANY', 'The company')}</h3>
                                {fact(t('TRADE.DRAWER.ACTIVITY', 'Activity'), <>{activityLabel(p.activity.code, t, p.activity.label)} <span className="text-muted-foreground">· {p.activity.code}</span></>)}
                                {p.address && fact(t('TRADE.DRAWER.ADDRESS', 'Address'), p.address)}
                                {fact(t('TRADE.DRAWER.DISTANCE', 'Distance'), formatKm(p.distanceKm, locale))}
                                {fact(t('TRADE.DRAWER.SIZE', 'Size'), headcountLabel(p.headcountBand, t, locale))}
                                {fact(t('TRADE.DRAWER.SINCE', 'In business since'), sinceYear(p.createdOn))}
                                {typeof p.establishments === 'number' && fact(t('TRADE.DRAWER.SITES', 'Open sites'), p.establishments)}
                                {p.soleTrader && fact(t('TRADE.DRAWER.LEGAL', 'Legal form'), t('TRADE.DRAWER.SOLE_TRADER', 'Sole trader'))}
                                {p.leaders.length > 0 && fact(
                                    t('TRADE.DRAWER.LEADERS', 'Run by'),
                                    <span className="flex flex-col">
                                        {p.leaders.map((l) => <span key={l.name}>{l.name}{l.role ? <span className="text-muted-foreground"> · {l.role}</span> : null}</span>)}
                                    </span>,
                                )}
                                {p.revenue && fact(
                                    t('TRADE.DRAWER.REVENUE', 'Turnover ({{year}})', { year: p.revenue.year }),
                                    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p.revenue.amount),
                                )}
                                {fact('SIREN', <span className="tabular-nums">{p.siren}</span>)}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button asChild variant="outline" size="sm">
                                        <a href={p.publicUrl} target="_blank" rel="noopener noreferrer" data-testid="trade-annuaire-link">
                                            <ExternalLink data-icon="inline-start" />
                                            {t('TRADE.DRAWER.REGISTRY_PAGE', 'Registry page')}
                                        </a>
                                    </Button>
                                    <Button asChild variant="outline" size="sm">
                                        <a href={webSearchHref(p.tradeName || p.name, p.city)} target="_blank" rel="noopener noreferrer">
                                            <Search data-icon="inline-start" />
                                            {t('TRADE.DRAWER.FIND_CONTACT', 'Find their phone or email')}
                                        </a>
                                    </Button>
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                    {t('TRADE.DRAWER.SOURCE', 'Public data from the French company register (SIRENE). It lists no phone numbers or emails.')}
                                </p>
                            </section>

                            <Separator />

                            <section>
                                <h3 className="mb-2 text-sm font-medium">{t('TRADE.DRAWER.NOTES', 'Notes')}</h3>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    onBlur={saveNotes}
                                    rows={4}
                                    placeholder={t('TRADE.DRAWER.NOTES_PLACEHOLDER', 'Who you spoke to, what they work on, when to call back…')}
                                />
                                <div className="mt-2 flex justify-end">
                                    <Button variant="outline" size="sm" onClick={saveNotes} disabled={savingNotes || notes === (p.notes ?? '')}>
                                        {savingNotes && <Spinner data-icon="inline-start" />}
                                        {t('TRADE.DRAWER.SAVE_NOTES', 'Save notes')}
                                    </Button>
                                </div>
                            </section>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default ProspectSheet;
